import React, { useState } from "react";
import { User, Info, MapPin, Pencil } from "lucide-react";
import axios from "axios";
import countriesData from "./countries.json";

const MultiStepForm = ({
  departureFlight,
  arrivalFlight,
  travellerInfo,
  contactInfo,
  billingInfo,
}) => {
  // ---- PRICE CALCULATION ----
  const departurePrice = departureFlight ? parseFloat(departureFlight.price) : 0;
  const arrivalPrice = arrivalFlight ? parseFloat(arrivalFlight.price) : 0;
  const basePrice = arrivalFlight ? arrivalPrice : departurePrice;
  const taxesAndFees = 15;
  const discount = 5;
  const totalPrice = (basePrice + taxesAndFees - discount).toFixed(2);

  // ---- COUNTRY / STATE MAP ----
  const countryStateMap = {};
  countriesData.forEach(({ name, states }) => {
    countryStateMap[name] = states.map((s) => s.name);
  });

  const [availableStates, setAvailableStates] = useState([]);

  // ---- STEPS STATE ----
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // ---- FORM DATA ----
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dob: "",
    address1: "",
    address2: "",
    city: "",
    zip: "",
    country: "",
    state: "",
  });

  const API_BASE_URL = "https://booktrip4me.com/api";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Step completion logic (used for summary text)
  const isStepCompleted = (step) => {
    switch (step) {
      case 1:
        return formData.email && formData.phone;
      case 2:
        return (
          formData.title &&
          formData.firstName &&
          formData.lastName &&
          formData.gender &&
          formData.dob
        );
      case 3:
        return (
          formData.address1 &&
          formData.city &&
          formData.country &&
          formData.state &&
          formData.zip
        );
      default:
        return false;
    }
  };

  const completeStep = (step) => {
    setCompletedSteps((prev) => [...new Set([...prev, step])]);
  };

  // ---- PARTIAL SUBMIT TO BACKEND ----
  const submitPartialData = async () => {
    try {
      const firstDep = departureFlight?.flights?.[0];
      const lastDep =
        departureFlight?.flights?.[departureFlight?.flights?.length - 1];

      const depDurationMin =
        (new Date(lastDep?.arrival_airport?.time) -
          new Date(firstDep?.departure_airport?.time)) /
        60000;
      const depHours = Math.floor(depDurationMin / 60);
      const depMinutes = depDurationMin % 60;

      const firstArr = arrivalFlight?.flights?.[0];
      const lastArr =
        arrivalFlight?.flights?.[arrivalFlight?.flights?.length - 1];

      const arrDurationMin =
        (new Date(lastArr?.arrival_airport?.time) -
          new Date(firstArr?.departure_airport?.time)) /
        60000;
      const arrHours = Math.floor(arrDurationMin / 60);
      const arrMinutes = arrDurationMin % 60;

      const search_id = localStorage.getItem("search_id");

      await axios.post(`${API_BASE_URL}/send-mails.php`, {
        search_id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address1: formData.address1,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        amount: totalPrice,
        order_id: `ORDER-${Date.now()}`,

        // Departure summary
        departure_time: new Date(
          firstDep?.departure_airport?.time
        ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        departure_airport: `${firstDep?.departure_airport?.name} (${firstDep?.departure_airport?.id})`,
        departure_duration: `${depHours}h ${depMinutes}m`,
        departure_stops: (departureFlight?.flights?.length || 1) - 1,
        departure_overnight: departureFlight?.overnight ? "Yes" : "No",
        arrival_time: new Date(
          lastDep?.arrival_airport?.time
        ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        arrival_airport: `${lastDep?.arrival_airport?.name} (${lastDep?.arrival_airport?.id})`,

        // Return summary (if round trip)
        return_departure_time: new Date(
          firstArr?.departure_airport?.time
        ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        return_departure_airport: `${firstArr?.departure_airport?.name} (${firstArr?.departure_airport?.id})`,
        return_duration: `${arrHours}h ${arrMinutes}m`,
        return_stops: (arrivalFlight?.flights?.length || 1) - 1,
        return_overnight: arrivalFlight?.overnight ? "Yes" : "No",
        return_arrival_time: new Date(
          lastArr?.arrival_airport?.time
        ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        return_arrival_airport: `${lastArr?.arrival_airport?.name} (${lastArr?.arrival_airport?.id})`,
      });
    } catch (err) {
      console.error("Error submitting partial data:", err);
    }
  };

  // ---- STEP HANDLERS ----
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (formData.email && formData.phone) {
      completeStep(1);
      setCurrentStep(2);
      await submitPartialData();
    }
  };

  const handleTravellerSubmit = async (e) => {
    e.preventDefault();
    if (
      formData.title &&
      formData.firstName &&
      formData.lastName &&
      formData.dob &&
      formData.gender
    ) {
      completeStep(2);
      setCurrentStep(3);
      await submitPartialData();
    }
  };

  const handlePaymentSubmit = async (e) => {
  e.preventDefault();

  if (
    formData.address1 &&
    formData.city &&
    formData.country &&
    formData.state &&
    formData.zip
  ) {
    completeStep(3);
    setCurrentStep(4);
  }

  // Build departure summary
  const firstDep = departureFlight?.flights?.[0];
  const lastDep = departureFlight?.flights?.[departureFlight?.flights?.length - 1];

  const depDurationMin =
    (new Date(lastDep?.arrival_airport?.time) -
      new Date(firstDep?.departure_airport?.time)) /
    60000;
  const depHours = Math.floor(depDurationMin / 60);
  const depMinutes = depDurationMin % 60;

  const departureData = {
    time: new Date(firstDep?.departure_airport?.time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    airport: `${firstDep?.departure_airport?.name} (${firstDep?.departure_airport?.id})`,
    arrival_time: new Date(lastDep?.arrival_airport?.time).toLocaleTimeString(
      [],
      { hour: "2-digit", minute: "2-digit" }
    ),
    arrival_airport: `${lastDep?.arrival_airport?.name} (${lastDep?.arrival_airport?.id})`,
    duration: `${depHours}h ${depMinutes}m`,
    stops: (departureFlight?.flights?.length || 1) - 1,
    overnight: departureFlight?.overnight ? "Yes" : "No",
  };

  // Build return summary (if exists)
  let returnData = null;
  if (arrivalFlight) {
    const firstArr = arrivalFlight?.flights?.[0];
    const lastArr =
      arrivalFlight?.flights?.[arrivalFlight?.flights?.length - 1];

    const arrDurationMin =
      (new Date(lastArr?.arrival_airport?.time) -
        new Date(firstArr?.departure_airport?.time)) /
      60000;
    const arrHours = Math.floor(arrDurationMin / 60);
    const arrMinutes = arrDurationMin % 60;

    returnData = {
      time: new Date(firstArr?.departure_airport?.time).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      airport: `${firstArr?.departure_airport?.name} (${firstArr?.departure_airport?.id})`,
      arrival_time: new Date(lastArr?.arrival_airport?.time).toLocaleTimeString(
        [],
        { hour: "2-digit", minute: "2-digit" }
      ),
      arrival_airport: `${lastArr?.arrival_airport?.name} (${lastArr?.arrival_airport?.id})`,
      duration: `${arrHours}h ${arrMinutes}m`,
      stops: (arrivalFlight?.flights?.length || 1) - 1,
      overnight: arrivalFlight?.overnight ? "Yes" : "No",
    };
  }

  const payload = {
    // CONTACT
    email: formData.email,
    phone: formData.phone,

    // TRAVELLER
    title: formData.title,
    first_name: formData.firstName,
    middle_name: formData.middleName,
    last_name: formData.lastName,
    gender: formData.gender,
    dob: formData.dob,

    // BILLING
    address1: formData.address1,
    address2: formData.address2,
    city: formData.city,
    state: formData.state,
    zip: formData.zip,
    country: formData.country,

    // AMOUNT
    amount: totalPrice,
    search_id: localStorage.getItem("search_id"),

    // FLIGHT DETAILS
    departure: departureData,
    return: returnData,
  };

  try {
    await axios.post(
      `${API_BASE_URL}/send-mail.php`,
      payload
    );
  } catch (err) {
    console.error("Email sending failed:", err);
  }

  // Store amount for payment.html
  localStorage.setItem("amount", totalPrice);

  // Redirect to Payment
  window.location.href = "/Payment.html";
};


  // ---- STEP HEADER COMPONENT ----
  const StepHeader = ({ step, title, icon, editable }) => {
    const isActive = currentStep === step;
    const done = isStepCompleted(step);

    return (
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div
            className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold ${
              done
                ? "bg-teal-500 text-white"
                : isActive
                ? "bg-teal-100 text-teal-700"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {step}
          </div>
          <div className="flex items-center gap-2 text-sm md:text-base font-semibold">
            {icon}
            <span
              className={
                isActive
                  ? "text-teal-700"
                  : done
                  ? "text-gray-700"
                  : "text-gray-400"
              }
            >
              {title}
            </span>
          </div>
        </div>

        {editable && step < currentStep && (
          <button
            onClick={() => setCurrentStep(step)}
            className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 transition flex items-center gap-1"
          >
            <Pencil size={14} />
            Edit
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full bg-white/90 border border-teal-100 rounded-2xl shadow-xl p-5 md:p-6 space-y-6">
      {/* Header + Price strip */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h4 className="text-lg md:text-xl font-bold text-gray-900">
          Traveler & Billing Details
        </h4>
        <div className="text-sm bg-teal-50 border border-teal-100 text-teal-700 px-3 py-1.5 rounded-full font-medium">
          Trip Total: <span className="font-bold">${totalPrice}</span> (incl.
          taxes & fees)
        </div>
      </div>

      {/* STEP 1 – CONTACT INFO */}
      <div className="space-y-2 border border-gray-100 rounded-xl p-4 md:p-5 bg-gray-50/60">
        <StepHeader
          step={1}
          title="Contact Information"
          icon={<User size={18} className="text-teal-600" />}
          editable
        />

        {(currentStep === 1 || currentStep > 1) && (
          <div className="mt-2 space-y-2">
            {currentStep === 1 ? (
              <form onSubmit={handleContactSubmit}>
                <div className="mb-4 flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-1/2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div className="w-full sm:w-1/2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Mobile Number
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Contact number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button className="w-full sm:w-1/3 bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition text-sm">
                    Continue to Traveler
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-xs md:text-sm text-gray-600 space-y-1">
                <p>Email: {formData.email}</p>
                <p>Phone: {formData.phone}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* STEP 2 – TRAVELER DETAILS */}
      <div className="space-y-2 border border-gray-100 rounded-xl p-4 md:p-5 bg-gray-50/60">
        <StepHeader
          step={2}
          title="Traveler Details"
          icon={<Info size={18} className="text-teal-600" />}
          editable
        />

        {currentStep === 2 ? (
          <div className="mt-2">
            <form onSubmit={handleTravellerSubmit}>
              {/* Title + First Name */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/4">
                  <label className="block text-xs text-gray-500 mb-1">
                    Title
                  </label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  >
                    <option value="">Select</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                </div>
                <div className="w-full sm:w-3/4">
                  <label className="block text-xs text-gray-500 mb-1">
                    First Name
                  </label>
                  <input
                    name="firstName"
                    type="text"
                    placeholder="As on passport"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Middle + Last Name */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Middle Name (optional)
                  </label>
                  <input
                    name="middleName"
                    type="text"
                    placeholder="Middle name"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Last Name
                  </label>
                  <input
                    name="lastName"
                    type="text"
                    placeholder="Surname"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Gender + DOB */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Date of Birth
                  </label>
                  <input
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button className="w-full sm:w-1/3 bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition text-sm">
                  Continue to Billing
                </button>
              </div>
            </form>
          </div>
        ) : (
          isStepCompleted(2) && (
            <div className="mt-2 text-xs md:text-sm text-gray-600 space-y-1">
              <p>
                {formData.title} {formData.firstName} {formData.middleName}{" "}
                {formData.lastName}
              </p>
              <p>Gender: {formData.gender}</p>
              <p>DOB: {formData.dob}</p>
            </div>
          )
        )}
      </div>

      {/* STEP 3 – BILLING / ADDRESS */}
      <div className="space-y-2 border border-gray-100 rounded-xl p-4 md:p-5 bg-gray-50/60">
        <StepHeader
          step={3}
          title="Billing & Address"
          icon={<MapPin size={18} className="text-teal-600" />}
          editable
        />

        {currentStep === 3 ? (
          <div className="mt-2">
            <form onSubmit={handlePaymentSubmit}>
              {/* Address 1 */}
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Address Line 1
                </label>
                <input
                  name="address1"
                  type="text"
                  placeholder="Street address, house no."
                  value={formData.address1}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  required
                />
              </div>

              {/* Address 2 */}
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">
                  Address Line 2 (optional)
                </label>
                <input
                  name="address2"
                  type="text"
                  placeholder="Apartment, suite, etc."
                  value={formData.address2}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                />
              </div>

              {/* City + Zip */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    City / Town
                  </label>
                  <input
                    name="city"
                    type="text"
                    placeholder="City / Town"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Zip / Postal Code
                  </label>
                  <input
                    name="zip"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Zip / Postal Code"
                    value={formData.zip}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Country + State */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Country
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={(e) => {
                      handleInputChange(e);
                      const selectedCountry = e.target.value;
                      setAvailableStates(countryStateMap[selectedCountry] || []);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                  >
                    <option value="">Select Country</option>
                    {Object.keys(countryStateMap).map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="block text-xs text-gray-500 mb-1">
                    State / Province
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    required
                    disabled={!formData.country}
                  >
                    <option value="">
                      {formData.country
                        ? "Select State"
                        : "Select Country first"}
                    </option>
                    {availableStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-4 flex-col sm:flex-row gap-3">
                <p className="text-xs text-gray-500">
                  By continuing, you agree to our Terms & Conditions and
                  Privacy Policy.
                </p>
                <button className="w-full sm:w-1/3 bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition text-sm">
                  Proceed to Payment
                </button>
              </div>
            </form>
          </div>
        ) : (
          isStepCompleted(3) && (
            <div className="mt-2 text-xs md:text-sm text-gray-600 space-y-1">
              <p>
                {formData.address1}
                {formData.address2 ? `, ${formData.address2}` : ""}
              </p>
              <p>
                {formData.city} {formData.zip}
              </p>
              <p>
                {formData.state}, {formData.country}
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MultiStepForm;
